import React from 'react';

import { Box, Popover as MuiPopover, Switch, Typography } from '@mui/material';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import type { Nullable } from '@clearverify/types';

import type { DemoCompanyEntityType } from 'src/constants/demoDashboardMode';
import { useDemoDashboardEntityToggle } from 'src/hooks/demoDashboardMode';

import { hotCornerStyles, popoverContentStyles, popoverLabelStyles } from './styles';

type DemoDashboardEntityToggleProps = {
  entityType: DemoCompanyEntityType;
  entityId: Nullable<string>;
};

export const DemoDashboardEntityToggle: React.FC<DemoDashboardEntityToggleProps> = ({
  entityType,
  entityId,
}) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'demo-dashboard-entity-toggle',
  });
  const { isAvailable, isEnabled, setEnabled } = useDemoDashboardEntityToggle(entityType, entityId);

  if (!isAvailable) return null;

  return (
    <React.Fragment>
      <Box
        component="button"
        type="button"
        className="demo-dashboard-hot-corner"
        {...bindTrigger(popupState)}
        sx={hotCornerStyles(isEnabled)}
        aria-label="Demo dashboard settings"
        title="Demo dashboard"
      />
      <MuiPopover
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 2,
          },
        }}
      >
        <Box sx={popoverContentStyles}>
          <Typography variant="caption" color="text.secondary" sx={popoverLabelStyles}>
            Demo dashboard
          </Typography>
          <Switch
            size="small"
            checked={isEnabled}
            onChange={(_, checked) => setEnabled(checked)}
            slotProps={{
              input: {
                'aria-label': 'Demo dashboard',
              },
            }}
          />
        </Box>
      </MuiPopover>
    </React.Fragment>
  );
};
